import React, { useState, useEffect, useCallback } from "react";
import customerAPI from "../api/customerAPI";
import { debounce } from "lodash";
import CustomerModal from "./CustomerModal";
import { useAuth } from "../context/AuthContext";

const CustomerSearch = ({
  onSelect,
  value = null,
  placeholder = "Search customer by name or email...",
  required = false,
  disabled = false,
  className = "",
}) => {
  const { isAuthReady, isAuthenticated, isRefreshRevoked } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(value);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notice, setNotice] = useState("");

  // Load initial customer if value is provided
  useEffect(() => {
    if (value && typeof value === "object") {
      setSelectedCustomer(value);
      setSearchTerm(value.label || value.name || "");
    } else if (!value) {
      setSelectedCustomer(null);
      setSearchTerm("");
    }
  }, [value]);

  // Debounced search
  const searchCustomers = useCallback(
    debounce(async (term) => {
      if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
        setCustomers([]);
        setShowDropdown(false);
        return;
      }

      if (!term || term.trim().length < 2) {
        setCustomers([]);
        return;
      }

      setLoading(true);
      try {
        const response = await customerAPI.search(term);
        const list = response?.data || response || [];
        setCustomers(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Error searching customers:", error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    }, 250),
    [isAuthReady, isAuthenticated, isRefreshRevoked]
  );

  const handleInputChange = (e) => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);

    if (selectedCustomer) {
      setSelectedCustomer(null);
      onSelect?.(null);
    }

    searchCustomers(term);
  };

  const handleSelectCustomer = (customer) => {
    if (selectedCustomer?._id && selectedCustomer._id === customer?._id) {
      setNotice("Customer already selected.");
    } else {
      setNotice("");
    }

    setSelectedCustomer(customer);
    setSearchTerm(customer.label || customer.name || "");
    setShowDropdown(false);
    setCustomers([]);
    onSelect?.(customer);
  };

  const handleClear = () => {
    setSelectedCustomer(null);
    setSearchTerm("");
    setCustomers([]);
    setShowDropdown(false);
    setNotice("");
    onSelect?.(null);
  };

  // click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".customer-search-container")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const showNoResults = showDropdown && searchTerm.trim().length >= 2 && customers.length === 0 && !loading;

  return (
    <div className={`customer-search-container relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled || !isAuthReady || !isAuthenticated || isRefreshRevoked}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
          autoComplete="off"
        />

        {selectedCustomer && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear selection"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {loading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {showDropdown && customers.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {customers.map((customer) => (
            <div
              key={customer._id}
              onClick={() => handleSelectCustomer(customer)}
              onMouseDown={(e) => e.preventDefault()}
              className="px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-teal-50 cursor-pointer"
            >
              <div className="font-medium text-gray-900">{customer.name}</div>
              <div className="text-sm text-gray-600">
                {customer.email && <span className="mr-3">Email: {customer.email}</span>}
                {customer.phone && <span>Phone: {customer.phone}</span>}
              </div>
              {(customer.address || customer.city) && (
                <div className="text-xs text-gray-500 mt-0.5"> {[customer.address, customer.city].filter(Boolean).join(", ")}</div>
              )}
              {customer.customerType && (
                <div className="text-xs text-teal-600 mt-1">Type: {customer.customerType}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No results + create */}
      {showNoResults && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 space-y-2">
          <div className="text-gray-500 text-center">No customers found for "{searchTerm}"</div>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowCreateModal(true)}
            className="w-full text-sm text-teal-700 font-medium hover:underline"
          >
            + Create new customer
          </button>
        </div>
      )}

      {/* Selected customer details */}
      {selectedCustomer && (
        <div className="mt-2 p-3 bg-teal-50 rounded-md border border-teal-200">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium text-teal-800">{selectedCustomer.name}</div>
              <div className="text-sm text-teal-600">
                {selectedCustomer.email && <div>Email: {selectedCustomer.email}</div>}
                {selectedCustomer.phone && <div>Phone: {selectedCustomer.phone}</div>}
                {selectedCustomer.mobile && <div>Mobile: {selectedCustomer.mobile}</div>}
                {selectedCustomer.tin && <div>TIN: {selectedCustomer.tin}</div>}
                {selectedCustomer.customerType && <div>Type: {selectedCustomer.customerType}</div>}
                {selectedCustomer.address && <div>Address: {selectedCustomer.address}</div>}
              </div>
            </div>
            <button type="button" onClick={handleClear} className="text-xs text-teal-600 hover:text-teal-800">
              Change
            </button>
          </div>
        </div>
      )}

      {notice && (
        <div className="mt-2 p-2 rounded-md border border-green-200 bg-green-50 text-green-700 text-sm">
          {notice}
        </div>
      )}

      {/* Create modal */}
      <CustomerModal
        isOpen={showCreateModal}
        initialName={searchTerm}
        onClose={() => setShowCreateModal(false)}
        onCreated={(customer) => {
          const alreadySelected = selectedCustomer?._id && selectedCustomer._id === customer?._id;
          setShowCreateModal(false);
          setNotice(alreadySelected ? "Customer already selected." : "Customer added successfully.");
          handleSelectCustomer(customer);
        }}
      />
    </div>
  );
};

export default CustomerSearch;
